import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import PropTypes from 'prop-types'

import PortalModal from 'components/Shared/PortalModal'
import CampaignBudget from 'components/Recommendations/CampaignBudget'
import CampaignGoals from 'components/Recommendations/CampaignGoals'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { SecondaryButton } from 'components/Shared/Button'
import Fade from 'components/Shared/Wrappers/Fade'
import { hasRunningCampaigns, uniqueTasteClusterCount, getCampaignRuntime } from 'api/models'
import { formatDateYYYYMMDD, truncateText } from 'utils/helpers'
import OpenCampaigns from './OpenCampaigns'
import { fetchEventBudgetMinMax, fetchEventSuggestedTasteClusters } from 'api/events'
import publishedCampaignSetupsApi from 'domain/publishedCampaignSetups/api'
import { initStateObject, objectReducer } from 'reducers/default'
import CampaignRuntime from './CampaignRuntime'
import InfoBox from 'components/Shared/InfoBox'
import useIsFirstRender from 'hooks/useFirstRender'
import { useItcStatus } from 'components/Recommendations/Itc/ItcStatusContextProvider'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'
import {
  useCampaignSetupDispatch,
  useCampaignSetup,
  useCurrentStepDispatch,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import useCustomTranslation from 'hooks/useCustomTranslation'
import { toSetupPlatformField } from 'hooks/usePublishedCampaignSetups'
import ConfirmDeselectTcModal from './ConfirmDeselectTcModal'
import Platforms from './Platforms'
import ConfirmSetupChangeModal from './ConfirmSetupChangeModal'
import {
  derivePlatformSelectionFromSetup,
  shouldConfirmSetupChange,
  platformIncludesGoogle,
  platformIncludesMeta,
  toApiPlatformField,
} from 'components/Recommendations/Campaign/platform'
import CampaignType from './CampaignType.jsx'
import {
  deriveCampaignTypeFromSetup,
  GoogleCampaignType,
  googleFirstHeadlineCharLimit,
  toApiCampaignType,
} from './campaignType'
import { droppedGatedPlatforms } from 'components/Recommendations/Campaign/Edit/CampaignForm/Targeting/targeting'
import { buildTargetingSkeleton } from './targetingSkeleton'
import {
  getRequiredSlotRatios,
  getGoogleExactRatios,
} from 'components/Recommendations/Campaign/Media/requiredSlotRatios'
import { pruneMediaForRequiredSlots } from 'components/Recommendations/Campaign/Media/pruneMediaForRequiredSlots'
import { augmentIncomingCreativeMedia } from 'components/Recommendations/Campaign/Media/mediaPayload'
import CoCreateBriefing from './CoCreateBriefing'

const calculateTotalRuntime = (startDate, endDate) => {
  return Math.round(new moment.duration(endDate - startDate).asDays())
}

// google's platform_overrides is meaningless once Google is no longer
// selected — send an explicit {} rather than omitting the key, since
// omitting it leaves whatever was previously saved (e.g. campaign_type)
// stale on the backend (PUT is a partial update; a field absent from the
// payload is left untouched, not cleared).
function buildPlatformOverridesConfig(platformValue, campaignTypeValue) {
  return {
    platform_overrides: platformIncludesGoogle(platformValue)
      ? { google: { campaign_type: toApiCampaignType(campaignTypeValue) } }
      : {},
  }
}

// call_to_action is either a plain string (legacy, shared across every
// platform — left untouched) or a {fb, google} object (CampaignForm.jsx's
// current write shape, see resolveCallToAction in utils/helpers.js) — drop
// whichever platform key is no longer selected so a stale CTA value doesn't
// linger for a platform that's no longer active. Returns `changed: false`
// when there's nothing to prune (string shape, or no stale key present).
function pruneCallToActionForPlatform(callToAction, platformValue) {
  if (!callToAction || typeof callToAction !== 'object') {
    return { value: callToAction, changed: false }
  }

  const dropFb = !platformIncludesMeta(platformValue) && 'fb' in callToAction
  const dropGoogle = !platformIncludesGoogle(platformValue) && 'google' in callToAction
  if (!dropFb && !dropGoogle) {
    return { value: callToAction, changed: false }
  }

  const next = { ...callToAction }
  if (dropFb) delete next.fb
  if (dropGoogle) delete next.google
  return { value: next, changed: true }
}

// description is a Meta-only field (campaign.js's validateCreative, and
// CampaignForm.jsx's DescriptionField only renders it when Meta is
// selected) — clear it once Google becomes the sole platform so a stale
// value doesn't linger for a platform that no longer uses it, mirroring
// pruneCallToActionForPlatform above.
function pruneDescriptionForPlatform(description, platformValue) {
  const isGoogleOnly = platformIncludesGoogle(platformValue) && !platformIncludesMeta(platformValue)
  if (!isGoogleOnly || !description) {
    return { value: description, changed: false }
  }

  return { value: '', changed: true }
}

// Google only accepts YouTube video references, Meta only accepts regular
// uploaded video files — drop whichever kind is no longer usable once
// platform switches to a single platform. Under "both" nothing is dropped.
// Must run before pruneMediaForRequiredSlots: that function ratio-matches
// candidates within a sub_group and could let an about-to-be-incompatible
// video win a slot over a compatible alternative if filtered afterward.
function pruneVideoForPlatform(media, platformValue) {
  return media.filter((item) => {
    const isYoutubeVideo = Boolean(item?.youtube_video_id)
    const isRegularVideo = item?.type?.startsWith('video/') && !isYoutubeVideo
    if (isYoutubeVideo && !platformIncludesGoogle(platformValue)) return false
    if (isRegularVideo && !platformIncludesMeta(platformValue)) return false
    return true
  })
}

// integration_details (ad_account/profile/pixel/etc.) is scoped per
// platform, same as targeting/platform_overrides/call_to_action above —
// without pruning, a deselected platform's entries linger in setup and fail
// validateIntegrations's accounts-pool lookup (Setup/Accounts.jsx's
// isStepValid(2)) once accounts for that platform are no longer fetched.
function pruneIntegrationDetailsForPlatform(integrationDetails, platformValue) {
  return (integrationDetails || []).filter((entry) => {
    if (entry.platform === 'google') return platformIncludesGoogle(platformValue)
    if (entry.platform === 'facebook' || entry.platform === 'instagram') {
      return platformIncludesMeta(platformValue)
    }
    return true
  })
}

// Google requires the first headline to be 30 (Display) or 40 (Demand Gen)
// characters or fewer (CampaignForm.jsx enforces this via the input's
// maxLength going forward, same hard character cut with no word-boundary
// awareness) — a headline[0] written while Meta-only (80-char cap) can be
// longer, so truncate it once Google joins the platform selection rather
// than leaving it silently over the limit. Truncating in place (vs.
// clearing) also keeps it non-empty, so the Google-required-headline[0]
// check in validateCreative doesn't leave the operator with a blocked
// Update button they have to fix by hand.
function truncateHeadlineForGoogle(headline, platformValue, campaignTypeValue) {
  const limit = googleFirstHeadlineCharLimit(campaignTypeValue)

  if (
    !platformIncludesGoogle(platformValue) ||
    !Array.isArray(headline) ||
    typeof headline[0] !== 'string' ||
    headline[0].length <= limit
  ) {
    return { value: headline, changed: false }
  }

  const next = [...headline]
  next[0] = truncateText(next[0], limit)
  return { value: next, changed: true }
}

// Google requires every body/description line to be 90 characters or fewer.
// Same truncate-in-place approach as truncateHeadlineForGoogle above.
function truncateBodyForGoogle(body, platformValue) {
  if (!platformIncludesGoogle(platformValue) || !Array.isArray(body)) {
    return { value: body, changed: false }
  }

  let changed = false
  const next = body.map((item) => {
    if (typeof item === 'string' && item.length > 90) {
      changed = true
      return truncateText(item, 90)
    }
    return item
  })

  return { value: changed ? next : body, changed }
}

// Applies the same headline[0]/body truncation to every localization —
// needed when both platforms stay selected (clearLocalizations only fires
// once Meta drops out entirely, see below), since a translated headline/body
// written under Meta's higher limits is just as over-the-cap for Google as
// the master language.
function truncateLocalizationsForGoogle(localizations, platformValue, campaignTypeValue) {
  if (
    !platformIncludesGoogle(platformValue) ||
    !localizations ||
    typeof localizations !== 'object'
  ) {
    return { value: localizations, changed: false }
  }

  let changed = false
  const next = Object.fromEntries(
    Object.entries(localizations).map(([locale, data]) => {
      const { value: headlines, changed: headlinesChanged } = truncateHeadlineForGoogle(
        data?.headlines,
        platformValue,
        campaignTypeValue,
      )
      const { value: bodies, changed: bodiesChanged } = truncateBodyForGoogle(
        data?.bodies,
        platformValue,
      )

      if (!headlinesChanged && !bodiesChanged) return [locale, data]

      changed = true
      return [locale, { ...data, headlines, bodies }]
    }),
  )

  return { value: changed ? next : localizations, changed }
}

const CampaignSetup = ({
  event,
  campaigns,
  suggestedTcs,
  selectedTcs,
  setSelectedTcs,
  initialSelectedTcs = [],
  initialBudget,
  initialGoal,
  dispatchSuggestedTcsAction,
  openCampaigns,
  refetchCampaigns,
  setSuggestedTcsChanged = () => {},
  publishedEventCampaign = null,
}) => {
  const { t } = useTranslation()
  const { setup } = useCampaignSetup()
  const isCampaignSetupInProgress = useMemo(() => !!setup, [setup])

  const { startSetup, reconfigureSetup, cancelPendingSync } = useCampaignSetupDispatch()
  const { goToNextStep } = useCurrentStepDispatch()
  const isFirstRender = useIsFirstRender()
  const hasWaveAccess = useHasPermission(Permissions.wave)
  const [budgetRange, dispatchBudgetRangeAction] = useReducer(objectReducer, initStateObject)
  const budgetLoadingRef = React.useRef(false)
  const defaultIntegrationDetails = React.useRef({
    integration_details: [],
    conversion_id: '',
    custom_event_type: '',
    lead_form_id: '',
  })
  const {
    mappedStatus: itcMappedStatus,
    loading: itcStatusLoading,
    error: itcStatusError,
  } = useItcStatus()
  const [eventBudget, setEventBudget] = useState(setup?.total_budget || initialBudget)
  const [eventGoal, setEventGoal] = useState(() => {
    if (!isCampaignSetupInProgress) return initialGoal

    const goalParts = setup?.goal?.split('.') ?? []
    return goalParts[goalParts.length - 1]
  })

  useEffect(() => {
    if (!isCampaignSetupInProgress) {
      setEventGoal(initialGoal)
    }
  }, [initialGoal, isCampaignSetupInProgress])

  const eventStartDate = moment(event.date)
  const showShortNoticeWarning = eventStartDate.diff(moment(), 'days') <= 7
  const totalUniqueTcs = uniqueTasteClusterCount(suggestedTcs.items)
  const [totalRuntime, setTotalRuntime] = useState(null)
  const [dates, setDates] = useState({
    from: null,
    to: null,
  })
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [deselectedTcs, setDeselectedTcs] = useState([])
  const publishedPlatform = useMemo(
    () =>
      publishedEventCampaign
        ? derivePlatformSelectionFromSetup({
            platform: toSetupPlatformField(publishedEventCampaign.platforms),
          })
        : null,
    [publishedEventCampaign],
  )
  const [platform, setPlatform] = useState(() =>
    setup
      ? derivePlatformSelectionFromSetup(setup)
      : (publishedPlatform ?? derivePlatformSelectionFromSetup(setup)),
  )
  // publishedEventCampaign arrives from its own async fetch (usePublishedCampaignSetups)
  // that isn't guaranteed to have resolved by the time the useState initializer above
  // ran — sync once it (or a draft setup starting) lands, same pattern as eventGoal/
  // initialGoal below.
  useEffect(() => {
    if (isCampaignSetupInProgress || !publishedPlatform) return
    setPlatform(publishedPlatform)
  }, [isCampaignSetupInProgress, publishedPlatform])
  const [pendingPlatform, setPendingPlatform] = useState(null)
  const [isPlatformChangeConfirmOpen, setIsPlatformChangeConfirmOpen] = useState(false)
  const publishedCampaignType = useMemo(
    () =>
      publishedEventCampaign
        ? deriveCampaignTypeFromSetup({
            platform_overrides: publishedEventCampaign.platformOverrides,
          })
        : null,
    [publishedEventCampaign],
  )
  const [campaignType, setCampaignType] = useState(() => deriveCampaignTypeFromSetup(setup))
  // publishedEventCampaign arrives from its own async fetch that isn't
  // guaranteed to have resolved by the time the useState initializer above
  // ran — same publishedPlatform/platform pattern above. Without this,
  // adding a new tc to an already-boosted Demand Gen event silently
  // defaulted campaignType to 'display' (no setup_process/draft exists yet
  // to read from), which then got written into the new setup_process at
  // creation and broke live publish for every ad group under it.
  useEffect(() => {
    if (isCampaignSetupInProgress || !publishedCampaignType) return
    setCampaignType(publishedCampaignType)
  }, [isCampaignSetupInProgress, publishedCampaignType])
  const [pendingCampaignType, setPendingCampaignType] = useState(null)
  const [isCampaignTypeChangeConfirmOpen, setIsCampaignTypeChangeConfirmOpen] = useState(false)
  const { getTranslation } = useCustomTranslation(event)
  useEffect(() => {
    if (typeof totalRuntime === 'number') {
      fetchEventBudgetMinMax(
        dispatchBudgetRangeAction,
        event.id,
        totalRuntime,
        platform,
        campaignType,
      )
    }
  }, [event.id, totalRuntime, platform, campaignType])

  useEffect(() => {
    fetchEventSuggestedTasteClusters(
      dispatchSuggestedTcsAction,
      event.id,
      eventBudget,
      eventGoal,
      platform,
    )
  }, [event.id, eventBudget, eventGoal, platform, dispatchSuggestedTcsAction])

  useEffect(() => {
    if (!isFirstRender) setSuggestedTcsChanged(true)
  }, [event.id, eventBudget, eventGoal, platform, setSuggestedTcsChanged, isFirstRender])

  useEffect(() => {
    const [runtimeStartDate, runtimeEndDate] = getCampaignRuntime(event)
    const todayDate = new Date()
    const startDate =
      setup?.start_date && new Date(setup.start_date) > todayDate
        ? new Date(setup.start_date)
        : runtimeStartDate.toDate()
    const savedEndDate = setup?.end_date ? new Date(setup.end_date) : runtimeEndDate.toDate()
    // Stale end dates can land before the corrected startDate — bump to the
    // day after start rather than send an invalid range.
    const endDate = moment(savedEndDate).isAfter(startDate, 'day')
      ? savedEndDate
      : moment(startDate).add(1, 'day').toDate()

    setDates({
      from: startDate,
      to: endDate,
    })
    setTotalRuntime(calculateTotalRuntime(startDate, endDate))
  }, [setup, event])

  useEffect(() => {
    if (defaultIntegrationDetails.current.integration_details.length > 0) {
      return
    }

    publishedCampaignSetupsApi.fetchDefaultParametersAsync(event.id).then((response) => {
      if (response.status === 200 && response.data.integration_details?.length > 0) {
        defaultIntegrationDetails.current = {
          integration_details: response.data.integration_details,
          conversion_id: response.data.conversion_id,
          lead_form_id: response.data.lead_form_id,
          custom_event_type: response.data.custom_event_type,
        }
      }
    })
  }, [event.id])

  const updateDate = (date, dateRangeString) => {
    const formattedDate = date ? moment(date).toDate() : ''

    const currentDateTime = { ...dates }
    currentDateTime[dateRangeString] = formattedDate
    const runtimeDuration = calculateTotalRuntime(currentDateTime.from, currentDateTime.to)

    setDates(currentDateTime)
    setTotalRuntime(runtimeDuration)
  }

  const startOrContinueSetup = useCallback(() => {
    if (
      itcStatusError ||
      itcStatusLoading ||
      ['running', 'failed', 'content_error'].includes(itcMappedStatus)
    ) {
      return
    }

    const selectedCampaigns = campaigns.items.filter((x) => selectedTcs.includes(x.tc))

    let config = {
      total_budget: eventBudget,
      goal: eventGoal,
      // Calendar dates, not instants — toISOString() converts to UTC and can
      // shift the calendar day the operator actually picked depending on the
      // browser's UTC offset. formatDateYYYYMMDD keeps the local date parts.
      start_date: dates.from ? formatDateYYYYMMDD(dates.from) : undefined,
      end_date: dates.to ? formatDateYYYYMMDD(dates.to) : undefined,
      creatives: selectedCampaigns.map((x) => ({
        tc: x.tc,
        tc_run_id: x.tc_run_id,
      })),
      targeting: buildTargetingSkeleton(selectedCampaigns, platform, setup?.targeting),
      platform: toApiPlatformField(platform),
      ...buildPlatformOverridesConfig(platform, campaignType),
    }

    // Clear campaign-wide geo targeting when any previously gated platforms change.
    if (droppedGatedPlatforms(setup, config).length) {
      config = { ...config, campaign_targeting: {} }
    }

    if (!isCampaignSetupInProgress) {
      config = {
        ...config,
        integration_details: defaultIntegrationDetails.current.integration_details,
        conversion_id: defaultIntegrationDetails.current.conversion_id,
        lead_form_id: defaultIntegrationDetails.current.lead_form_id,
        custom_event_type: defaultIntegrationDetails.current.custom_event_type,
      }
    }

    if (isCampaignSetupInProgress) {
      reconfigureSetup(config, (result) => {
        if (result.success) {
          goToNextStep()
        }
      })
    } else {
      startSetup(config, (result) => {
        if (result.success) {
          goToNextStep()
        }
      })
    }
  }, [
    eventBudget,
    eventGoal,
    dates,
    selectedTcs,
    campaigns.items,
    goToNextStep,
    isCampaignSetupInProgress,
    itcMappedStatus,
    itcStatusError,
    itcStatusLoading,
    reconfigureSetup,
    startSetup,
    platform,
    campaignType,
    setup,
  ])

  // Writes platform and/or campaign-type selection to an already-existing
  // setup process immediately, rather than waiting for "Next" —
  // setup_process.platform and .platform_overrides.google.campaign_type are
  // the sole source of truth read by all later steps, so a setup that already
  // exists must never sit with a stale value for either between changes.
  const writeThroughPlatformAndCampaignType = useCallback(
    (platformValue, campaignTypeValue) => {
      if (!isCampaignSetupInProgress) return

      // A stale debounced per-TC autosync (CampaignSetupContext.jsx's
      // debouncedSync, scheduled by a creative edit on step 3) can otherwise
      // fire after this immediate write with a pre-switch `setup` snapshot
      // and re-introduce media the prune below just removed.
      cancelPendingSync()

      const selectedCampaigns = campaigns.items.filter((x) => selectedTcs.includes(x.tc))
      const requiredSlotRatios = getRequiredSlotRatios(platformValue, campaignTypeValue)
      const googleExactRatios = getGoogleExactRatios(platformValue, campaignTypeValue)

      const creatives = selectedCampaigns.map((x) => {
        const currentCreative = setup?.creatives?.find(
          (c) => c.tc === x.tc && c.tc_run_id === x.tc_run_id,
        )

        // Carousel ads are supported by Google only for Demand Gen campaigns
        // (not Display) — dropped whenever Google joins the platform
        // selection with a non-Demand-Gen campaign type, even for a TC with
        // no media/placement_media of its own (carousel uses carousel_cards,
        // not media).
        const clearCarousel =
          platformIncludesGoogle(platformValue) &&
          campaignTypeValue !== GoogleCampaignType.demandGen &&
          currentCreative?.carousel_cards?.length > 0

        const { value: prunedCallToAction, changed: ctaChanged } = pruneCallToActionForPlatform(
          currentCreative?.call_to_action,
          platformValue,
        )

        const { value: prunedDescription, changed: descriptionChanged } =
          pruneDescriptionForPlatform(currentCreative?.description, platformValue)

        // Google-only doesn't support per-language creatives (LanguageTabs
        // blocks adding a language once Google is the sole platform) — drop
        // any localizations left over from a prior Meta/both selection so
        // they don't linger orphaned once Meta is deselected.
        const clearLocalizations =
          platformIncludesGoogle(platformValue) &&
          !platformIncludesMeta(platformValue) &&
          currentCreative?.localizations &&
          Object.keys(currentCreative.localizations).length > 0

        const { value: truncatedHeadline, changed: headlineChanged } = truncateHeadlineForGoogle(
          currentCreative?.headline,
          platformValue,
          campaignTypeValue,
        )
        const { value: truncatedBody, changed: bodyChanged } = truncateBodyForGoogle(
          currentCreative?.body,
          platformValue,
        )
        // clearLocalizations already wipes localizations outright below —
        // truncating them too would be pointless (and would fight over the
        // `localizations` key with clearLocalizations's own {} value).
        const { value: truncatedLocalizations, changed: localizationsChanged } = clearLocalizations
          ? { value: currentCreative?.localizations, changed: false }
          : truncateLocalizationsForGoogle(
              currentCreative?.localizations,
              platformValue,
              campaignTypeValue,
            )

        if (!currentCreative?.media?.length && !currentCreative?.placement_media?.length) {
          return {
            tc: x.tc,
            tc_run_id: x.tc_run_id,
            ...(clearCarousel ? { carousel_cards: [] } : {}),
            ...(ctaChanged ? { call_to_action: prunedCallToAction } : {}),
            ...(descriptionChanged ? { description: prunedDescription } : {}),
            ...(headlineChanged ? { headline: truncatedHeadline } : {}),
            ...(bodyChanged ? { body: truncatedBody } : {}),
            ...(clearLocalizations
              ? { localizations: {} }
              : localizationsChanged
                ? { localizations: truncatedLocalizations }
                : {}),
          }
        }

        // augmentIncomingCreativeMedia migrates the legacy media+placement_media
        // shape first (mirrors CampaignForm.jsx's initialMedia prep) so
        // sub_group_id grouping below is correct even for a TC never opened
        // in step 3 this session. pruneVideoForPlatform runs before the
        // ratio-slot pruning so an incompatible video never wins a slot over
        // a compatible alternative in the same sub_group.
        const prunedMedia = pruneMediaForRequiredSlots(
          pruneVideoForPlatform(augmentIncomingCreativeMedia(currentCreative), platformValue),
          requiredSlotRatios,
          googleExactRatios,
        )

        return {
          tc: x.tc,
          tc_run_id: x.tc_run_id,
          media: prunedMedia,
          placement_media: [],
          ...(clearCarousel ? { carousel_cards: [] } : {}),
          ...(ctaChanged ? { call_to_action: prunedCallToAction } : {}),
          ...(descriptionChanged ? { description: prunedDescription } : {}),
          ...(headlineChanged ? { headline: truncatedHeadline } : {}),
          ...(bodyChanged ? { body: truncatedBody } : {}),
          ...(clearLocalizations
            ? { localizations: {} }
            : localizationsChanged
              ? { localizations: truncatedLocalizations }
              : {}),
        }
      })

      const platformOverridesConfig = buildPlatformOverridesConfig(platformValue, campaignTypeValue)

      // Without these, {...setup, ...config} would resend setup's stale
      // persisted start_date/end_date untouched.
      const startDate =
        dates.from && moment(dates.from).isBefore(moment(), 'day') ? new Date() : dates.from

      const endDate =
        dates.to && moment(dates.to).isAfter(startDate, 'day')
          ? dates.to
          : moment(startDate).add(1, 'day').toDate()

      reconfigureSetup({
        platform: toApiPlatformField(platformValue),
        creatives,
        integration_details: pruneIntegrationDetailsForPlatform(
          setup?.integration_details,
          platformValue,
        ),
        targeting: buildTargetingSkeleton(selectedCampaigns, platformValue, setup?.targeting),
        // google.campaign_type is required (validate_setup_input,
        // fd-client-api-google-ads) once Google is selected and creative
        // content exists — switching platform to include Google without
        // this would 400 on the very next creative/targeting save. Going
        // the other way (Google deselected), buildPlatformOverridesConfig
        // sends an explicit {} so a stale campaign_type from before doesn't
        // linger under a platform that's no longer selected.
        ...platformOverridesConfig,
        campaign_targeting: {},
        start_date: startDate ? formatDateYYYYMMDD(startDate) : undefined,
        end_date: endDate ? formatDateYYYYMMDD(endDate) : undefined,
      })
    },
    [
      isCampaignSetupInProgress,
      campaigns.items,
      selectedTcs,
      setup,
      reconfigureSetup,
      cancelPendingSync,
      dates,
    ],
  )

  const applyPlatformChange = useCallback(
    (nextPlatform) => {
      setPlatform(nextPlatform)
      writeThroughPlatformAndCampaignType(nextPlatform, campaignType)
    },
    [campaignType, writeThroughPlatformAndCampaignType],
  )

  const applyCampaignTypeChange = useCallback(
    (nextCampaignType) => {
      setCampaignType(nextCampaignType)
      writeThroughPlatformAndCampaignType(platform, nextCampaignType)
    },
    [platform, writeThroughPlatformAndCampaignType],
  )

  const handlePlatformSelected = useCallback(
    (nextPlatform) => {
      if (
        shouldConfirmSetupChange(isCampaignSetupInProgress, selectedTcs, platform, nextPlatform)
      ) {
        setPendingPlatform(nextPlatform)
        setIsPlatformChangeConfirmOpen(true)
        return
      }
      applyPlatformChange(nextPlatform)
    },
    [isCampaignSetupInProgress, selectedTcs, platform, applyPlatformChange],
  )

  const handleCampaignTypeSelected = useCallback(
    (nextCampaignType) => {
      if (
        shouldConfirmSetupChange(
          isCampaignSetupInProgress,
          selectedTcs,
          campaignType,
          nextCampaignType,
        )
      ) {
        setPendingCampaignType(nextCampaignType)
        setIsCampaignTypeChangeConfirmOpen(true)
        return
      }
      applyCampaignTypeChange(nextCampaignType)
    },
    [isCampaignSetupInProgress, selectedTcs, campaignType, applyCampaignTypeChange],
  )

  const handleNextClick = useCallback(() => {
    const deselectedTcs =
      Array.isArray(initialSelectedTcs) && initialSelectedTcs.length
        ? initialSelectedTcs.filter((tc) => !selectedTcs.includes(tc))
        : []

    if (isCampaignSetupInProgress && deselectedTcs.length) {
      setDeselectedTcs(deselectedTcs)
      setIsConfirmModalOpen(true)
      return
    }

    startOrContinueSetup()
  }, [initialSelectedTcs, selectedTcs, startOrContinueSetup, isCampaignSetupInProgress])

  const eventHasRunningCampaigns = useMemo(
    () => hasRunningCampaigns(campaigns.items),
    [campaigns.items],
  )
  return (
    <div className="d-flex flex-column gap-5 p-0">
      <Fade transitionDuration={500}>
        <div className="d-flex flex-column gap-5">
          {showShortNoticeWarning && (
            <TipDialogueBox
              text={getTranslation('Recommendations.shortNoticeWarning.content')}
              variant="warning-light"
            />
          )}

          <div className="d-flex flex-column flex-lg-row my-1 order-1 gap-4">
            <div className="flex-1 col-12 col-lg-6 px-0 mb-3 mb-lg-0">
              <Platforms
                selectedPlatform={platform}
                onPlatformSelected={handlePlatformSelected}
                disabled={eventHasRunningCampaigns || !!publishedEventCampaign}
              />
            </div>
            <div className="flex-1 col-12 col-lg-6 px-0">
              <CampaignGoals
                event={event}
                budget={eventBudget}
                eventId={event.id}
                onGoalSelected={setEventGoal}
                selectedGoal={eventGoal}
                disabled={eventHasRunningCampaigns}
              />
            </div>
          </div>

          {platformIncludesGoogle(platform) && (
            <div className="d-flex flex-column my-1 order-1">
              <CampaignType
                selectedCampaignType={campaignType}
                onCampaignTypeSelected={handleCampaignTypeSelected}
                disabled={eventHasRunningCampaigns}
              />
            </div>
          )}

          <div className="d-flex flex-column my-1 order-2 order-lg-1">
            <CampaignBudget
              selectedBudget={eventBudget}
              minimumBudget={budgetRange.content.budget_abs_min || eventBudget}
              maximumBudget={budgetRange.content.budget_max}
              recommendedMinimumBudget={budgetRange.content.budget_min}
              onChangeQueued={() => (budgetLoadingRef.current = true)}
              onBudgetChanged={(b) => {
                setEventBudget(b)
                budgetLoadingRef.current = false
              }}
              addressedTasteClusters={uniqueTasteClusterCount(suggestedTcs.items)}
            >
              <InfoBox
                title={t('Tips.Recommendations.CampaignBudget.title')}
                body={
                  <div>
                    <p>{getTranslation('Tips.Recommendations.CampaignBudget.body1')}</p>
                    <br />
                    <p>{t('Tips.Recommendations.CampaignBudget.body2')}</p>
                  </div>
                }
                placement="bottom"
              >
                <h2 className="d-inline-block section-title">
                  {t(
                    `Recommendations.campaignBudget.${eventHasRunningCampaigns ? 'selectBudgetForNewAudiences' : 'selectLabel'}`,
                  )}
                </h2>
              </InfoBox>
            </CampaignBudget>
          </div>
        </div>
      </Fade>

      <CampaignRuntime
        event={event}
        updateDate={updateDate}
        dates={dates}
        totalRuntime={totalRuntime}
      />

      <CoCreateBriefing eventId={event.id} />
 
      <OpenCampaigns
        eventId={event.id}
        campaigns={openCampaigns}
        selectedTcs={selectedTcs}
        setSelectedTcs={setSelectedTcs}
        totalUniqueTcs={totalUniqueTcs}
        setupProcessId={isCampaignSetupInProgress ? setup.id : null}
        eventHasActiveCampaigns={eventHasRunningCampaigns}
        refetchCampaigns={refetchCampaigns}
      />
      <div className="recommendations-page_cta-btns_row order-1 order-lg-3">
        <SecondaryButton
          disabled={
            !selectedTcs.length ||
            !hasWaveAccess ||
            budgetLoadingRef.current === true ||
            itcStatusError ||
            itcStatusLoading ||
            ['running', 'failed', 'content_error'].includes(itcMappedStatus)
          }
          disabledTitle={!selectedTcs.length ? '' : null}
          className="bt"
          onClick={handleNextClick}
          text={
            isCampaignSetupInProgress
              ? t('Recommendations.btnContinueSetup')
              : t('Recommendations.btnStartSetup')
          }
        />
      </div>
      <PortalModal isOpen={isConfirmModalOpen} onClickOutside={() => setIsConfirmModalOpen(false)}>
        <ConfirmDeselectTcModal
          tcs={deselectedTcs}
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={() => {
            setIsConfirmModalOpen(false)
            startOrContinueSetup()
          }}
        />
      </PortalModal>
      <PortalModal
        isOpen={isPlatformChangeConfirmOpen}
        onClickOutside={() => {
          setIsPlatformChangeConfirmOpen(false)
          setPendingPlatform(null)
        }}
      >
        <ConfirmSetupChangeModal
          title={t('Recommendations.confirmPlatformChange.title')}
          body={t('Recommendations.confirmPlatformChange.body')}
          onCancel={() => {
            setIsPlatformChangeConfirmOpen(false)
            setPendingPlatform(null)
          }}
          onConfirm={() => {
            applyPlatformChange(pendingPlatform)
            setIsPlatformChangeConfirmOpen(false)
            setPendingPlatform(null)
          }}
        />
      </PortalModal>
      <PortalModal
        isOpen={isCampaignTypeChangeConfirmOpen}
        onClickOutside={() => {
          setIsCampaignTypeChangeConfirmOpen(false)
          setPendingCampaignType(null)
        }}
      >
        <ConfirmSetupChangeModal
          title={t('Recommendations.confirmCampaignTypeChange.title')}
          body={t('Recommendations.confirmCampaignTypeChange.body')}
          onCancel={() => {
            setIsCampaignTypeChangeConfirmOpen(false)
            setPendingCampaignType(null)
          }}
          onConfirm={() => {
            applyCampaignTypeChange(pendingCampaignType)
            setIsCampaignTypeChangeConfirmOpen(false)
            setPendingCampaignType(null)
          }}
        />
      </PortalModal>
    </div>
  )
}

CampaignSetup.propTypes = {
  event: PropTypes.object,
  setup: PropTypes.object,
  campaigns: PropTypes.object,
  suggestedTcs: PropTypes.object,
  selectedTcs: PropTypes.arrayOf(Object),
  setSelectedTcs: PropTypes.func,
  initialSelectedTcs: PropTypes.arrayOf(PropTypes.string),
  initialBudget: PropTypes.number,
  initialGoal: PropTypes.string,
  dispatchSuggestedTcsAction: PropTypes.func,
  dispatchSetupAction: PropTypes.func,
  openCampaigns: PropTypes.arrayOf(Object),
  isCampaignSetupInProgress: PropTypes.bool,
  setSuggestedTcsChanged: PropTypes.func,
  refetchCampaigns: PropTypes.func.isRequired,
  publishedEventCampaign: PropTypes.object,
}

export default CampaignSetup
